import React, { useState, useEffect } from 'react';
import {
    Box,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    IconButton,
    Typography,
    Snackbar,
    Alert,
    MenuItem
} from '@mui/material';
import {
    Delete as DeleteIcon,
    Add as AddIcon
} from '@mui/icons-material';

// Получаем IP-адрес сервера из URL
const API_URL = (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1")
  ? window.location.protocol + '//' + window.location.hostname + ':9000'
  : '';

const getRoleName = (access_level) => {
    switch (String(access_level)) {
        case "1":
            return "Адміністратор";
        case "2":
            return "Редактор";
        case "3":
            return "Користувач";
        default:
            return "Невідомо";
    }
};

function UserManagement() {
    const [users, setUsers] = useState([]);
    const [openDialog, setOpenDialog] = useState(false);
    const [formData, setFormData] = useState({
        username: '',
        password: '',
        access_level: "2"
    });
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success'
    });

    useEffect(() => {
        fetchUsers();
    }, []);

    const fetchUsers = async () => {
        try {
            const currentUser = JSON.parse(localStorage.getItem('user'));
            if (!currentUser) {
                console.error('Користувача не знайдено');
                return;
            }

            // console.log('Відправка запиту на отримання користувачів...');
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_URL}/api/users`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            // console.log('Статус відповіді:', response.status);
            if (response.ok) {
                const data = await response.json();
                // console.log('Отримані дані користувачів:', data);
                setUsers(data);
            } else {
                throw new Error('Не вдалося отримати список користувачів');
            }
        } catch (error) {
            console.error('Помилка при отриманні користувачів:', error);
            setSnackbar({
                open: true,
                message: error.message,
                severity: 'error'
            });
        }
    };

    const handleOpenDialog = () => {
        setFormData({
            username: '',
            password: '',
            access_level: "2"
        });
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setFormData({
            username: '',
            password: '',
            access_level: "2"
        });
    };

    const handleSave = async () => {
        try {
            const currentUser = JSON.parse(localStorage.getItem('user'));
            if (!currentUser) {
                throw new Error('Користувача не знайдено');
            }

            const token = localStorage.getItem('token');
            const response = await fetch(`${API_URL}/api/users`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(formData)
            });

            if (!response.ok) {
                throw new Error('Помилка створення користувача');
            }

            setSnackbar({
                open: true,
                message: 'Користувача створено',
                severity: 'success'
            });

            handleCloseDialog();
            fetchUsers();
        } catch (error) {
            console.error('Помилка при створенні користувача:', error);
            setSnackbar({
                open: true,
                message: error.message,
                severity: 'error'
            });
        }
    };

    const handleDelete = async (userId) => {
        if (!window.confirm('Ви впевнені, що хочете видалити цього користувача?')) {
            return;
        }

        try {
            const currentUser = JSON.parse(localStorage.getItem('user'));
            if (!currentUser) {
                throw new Error('Користувача не знайдено');
            }

            // console.log('Отправка запроса на удаление пользователя:', userId);
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_URL}/api/users/${userId}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (!response.ok) {
                throw new Error('Помилка видалення користувача');
            }

            setSnackbar({
                open: true,
                message: 'Користувача видалено',
                severity: 'success'
            });

            fetchUsers();
        } catch (error) {
            console.error('Помилка при видаленні користувача:', error);
            setSnackbar({
                open: true,
                message: error.message,
                severity: 'error'
            });
        }
    };

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h5">Управління користувачами</Typography>
                <Button
                    variant="contained"
                    color="primary"
                    startIcon={<AddIcon />}
                    onClick={handleOpenDialog}
                >
                    Додати користувача
                </Button>
            </Box>

            <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Ім'я користувача</TableCell>
                            <TableCell>Рівень доступу</TableCell>
                            <TableCell align="right">Дії</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {users.map((user) => (
                            <TableRow key={user.id}>
                                <TableCell>{user.username}</TableCell>
                                <TableCell>{getRoleName(user.access_level)}</TableCell>
                                <TableCell align="right">
                                    <IconButton
                                        color="error"
                                        onClick={() => handleDelete(user.id)}
                                        title="Видалити"
                                    >
                                        <DeleteIcon />
                                    </IconButton>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            <Dialog open={openDialog} onClose={handleCloseDialog}>
                <DialogTitle>Додавання користувача</DialogTitle>
                <DialogContent>
                    <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <TextField
                            fullWidth
                            label="Ім'я користувача"
                            value={formData.username}
                            onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                            required
                        />
                        <TextField
                            fullWidth
                            type="password"
                            label="Пароль"
                            value={formData.password}
                            onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                            required
                        />
                        <TextField
                            select
                            fullWidth
                            label="Роль користувача"
                            value={formData.access_level}
                            onChange={(e) => setFormData({ ...formData, access_level: e.target.value })}
                            required
                        >
                            <MenuItem value="1">Адміністратор (повний доступ)</MenuItem>
                            <MenuItem value="2">Редактор (перегляд та редагування)</MenuItem>
                            <MenuItem value="3">Користувач (тільки перегляд)</MenuItem>
                        </TextField>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog}>Скасувати</Button>
                    <Button onClick={handleSave} variant="contained" color="primary">
                        Створити
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
            >
                <Alert
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                    severity={snackbar.severity}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}

export default UserManagement; 